import React, {useEffect, useState} from 'react';
import {useSelector} from 'react-redux';
import {useParams, useHistory, Link} from 'react-router-dom';
import styled from 'styled-components';
import {Alert, Button, Card, Dialog} from '@betareader/design-system';
import Icon from '../../components/AppIcon';
import InviteReader from '../readerIndex/inviteReader';
import {
  createSample,
  updateSample,
  getSample,
  generateAudio,
  getStats,
  listVoices,
  listSourceBooks,
  getSourceChapter,
} from './api';

const Page = styled.div`
  padding: 20px;
  max-width: 900px;
  margin: 0 auto;
`;

const Row = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  align-items: center;
  margin: 12px 0;
`;

const StatBox = styled(Card)`
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
`;

const ContactRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 0;
  border-bottom: 1px solid rgba(34, 36, 38, 0.1);

  &:last-child {
    border-bottom: none;
  }
`;

const ContactDetails = styled.div`
  flex: 1;
  min-width: 0;
`;

const Muted = styled.div`
  color: var(--br-muted);
  font-size: 0.9em;
  word-break: break-word;
`;

const Quote = styled.em`
  display: block;
  margin-top: 4px;
`;

const Field = styled.div`
  display: grid;
  gap: 4px;
  margin-bottom: 12px;
  min-width: 0;
  input:not([type='checkbox']), textarea, select {
    width: 100%;
    box-sizing: border-box;
    padding: 8px 10px;
    border: 1px solid var(--br-border);
    border-radius: 7px;
    background: var(--br-surface);
    color: var(--br-text);
    font: inherit;
  }
  input[type='checkbox'] {
    margin-right: 8px;
  }
`;

const Badge = styled.span`
  display: inline-block;
  padding: 2px 7px;
  border-radius: 6px;
  background: var(--br-raised-surface);
  color: var(--br-text);
`;

const SampleChapterEditor = () => {
  const idToken = useSelector((s) => s.user.idToken);
  const {id} = useParams();
  const history = useHistory();
  const isNative = Boolean(window.ReactNativeWebView);
  const [form, setForm] = useState({
    title: '',
    variantLabel: 'A',
    description: '',
    genres: [],
    language: 'en',
    contentText: '',
    listed: false,
  });
  const [sample, setSample] = useState(null);
  const [stats, setStats] = useState(null);
  const [voicesInfo, setVoicesInfo] = useState({configured: false, voices: []});
  const [voiceId, setVoiceId] = useState(undefined);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [sourceBooks, setSourceBooks] = useState(null);
  const [pickedBookId, setPickedBookId] = useState(null);
  const [pickedVersionId, setPickedVersionId] = useState(null);
  // The reader currently being invited to a book, or null when the modal is
  // closed. Holds the `{_id, displayName}` shape InviteReader expects.
  const [invitee, setInvitee] = useState(null);
  const [pickedChapterId, setPickedChapterId] = useState(null);
  const [pickerLoading, setPickerLoading] = useState(false);

  const isNew = !id;

  const load = async () => {
    if (!id) return;
    try {
      const s = await getSample(idToken, id);
      setSample(s);
      setForm({
        title: s.title || '',
        variantLabel: s.variantLabel || 'A',
        description: s.description || '',
        genres: s.genres || [],
        language: s.language || 'en',
        contentText: s.contentText || '',
        listed: !!s.listed,
      });
      setVoiceId(s.audioVoiceId);
      const st = await getStats(idToken, id);
      setStats(st);
    } catch (e) {
      setError(e.message);
    }
  };

  const loadVoices = async () => {
    try {
      const v = await listVoices(idToken);
      setVoicesInfo(v);
    } catch (e) {
      // non-fatal
    }
  };

  useEffect(() => {
    if (idToken) {
      load();
      loadVoices();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idToken, id]);

  const handleSave = async () => {
    setBusy(true);
    setError(null);
    try {
      if (isNew) {
        const created = await createSample(idToken, form);
        history.replace(`/sample-chapters/edit/${created._id}`);
      } else {
        const updated = await updateSample(idToken, id, form);
        setSample(updated);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const openPicker = async () => {
    setPickerOpen(true);
    setError(null);
    if (!sourceBooks) {
      setPickerLoading(true);
      try {
        const books = await listSourceBooks(idToken);
        setSourceBooks(books);
      } catch (e) {
        setError(e.message);
      } finally {
        setPickerLoading(false);
      }
    }
  };

  const handleCopyChapter = async () => {
    if (!pickedBookId || !pickedChapterId) return;
    setPickerLoading(true);
    setError(null);
    try {
      const chapter = await getSourceChapter(
        idToken,
        pickedBookId,
        pickedChapterId
      );
      setForm((prev) => ({
        ...prev,
        title: prev.title || chapter.title || '',
        contentText: chapter.contentText || '',
      }));
      setPickerOpen(false);
      setPickedBookId(null);
      setPickedVersionId(null);
      setPickedChapterId(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setPickerLoading(false);
    }
  };

  const handleGenerate = async () => {
    if (!sample) return;
    setBusy(true);
    setError(null);
    try {
      // Note: in production a Paddle one-off purchase flow runs first;
      // here we send a placeholder paymentToken and rely on the server's
      // BR_TTS_PAYMENT_BYPASS flag for local dev.
      await generateAudio(idToken, sample._id, {
        voiceId,
        paymentToken: 'dev-placeholder',
      });
      // poll for status
      const interval = setInterval(async () => {
        try {
          const updated = await getSample(idToken, sample._id);
          setSample(updated);
          if (updated.ttsStatus === 'ready' || updated.ttsStatus === 'failed') {
            clearInterval(interval);
            setBusy(false);
          }
        } catch (e) {
          clearInterval(interval);
          setBusy(false);
        }
      }, 3000);
    } catch (e) {
      setError(e.message);
      setBusy(false);
    }
  };

  if (!isNew && !sample) return <p role="status">Loading sample chapter…</p>;

  const voiceOptions = voicesInfo.voices;

  // The API splits raters in two and that split is the privacy model: readers
  // with a live opt-in come back identified under `contacts`, everyone else
  // comes back anonymised under `ratings` with no uid at all. Do not try to
  // re-derive one from the other here — the client is not the filter.
  const contacts = (stats && stats.contacts) || [];
  const anonymousRatings = (stats && stats.ratings) || [];

  return (
    <Page>
      <Link to="/sample-chapters" className="br-button br-button--ghost br-button--small">
        <Icon name="arrow left" /> Back
      </Link>
      <h1>
        {isNew ? 'New sample chapter' : 'Edit sample chapter'}{' '}
        {!isNew && <Badge>Variant {form.variantLabel}</Badge>}
      </h1>
      {error && <Alert variant="error">{error}</Alert>}

      <form onSubmit={(event) => { event.preventDefault(); handleSave(); }}>
        <Field>
          <label htmlFor="sample-title">Title</label>
          <input
            id="sample-title"
            value={form.title}
            onChange={(e) => setForm({...form, title: e.target.value})}
          />
        </Field>
        <Row>
          <Field>
            <label htmlFor="sample-variant">Variant label (e.g. A, B, "Action opening")</label>
            <input
              id="sample-variant"
              value={form.variantLabel}
              onChange={(e) => setForm({...form, variantLabel: e.target.value})}
            />
          </Field>
          <Field>
            <label htmlFor="sample-language">Language</label>
            <input
              id="sample-language"
              value={form.language}
              onChange={(e) => setForm({...form, language: e.target.value})}
            />
          </Field>
        </Row>
        <Field>
          <label htmlFor="sample-description">Short description (shown in discover)</label>
          <textarea
            id="sample-description"
            rows={3}
            value={form.description}
            onChange={(e) => setForm({...form, description: e.target.value})}
          />
        </Field>
        <Field>
          <label htmlFor="sample-content">Chapter text</label>
          <div style={{marginBottom: 8}}>
            <Button variant="secondary" size="small" onClick={openPicker}>
              <Icon name="copy" /> Copy from one of my books
            </Button>
          </div>
          <textarea
            id="sample-content"
            rows={20}
            value={form.contentText}
            onChange={(e) => setForm({...form, contentText: e.target.value})}
          />
          <small>
            {(form.contentText || '').trim().split(/\s+/).filter(Boolean).length}{' '}
            words
          </small>
        </Field>
        <Field>
          <label>
            <input
              type="checkbox"
              checked={form.listed}
              onChange={(event) => setForm({...form, listed: event.target.checked})}
            />
            List publicly so readers can find and rate this sample
          </label>
        </Field>

        <Button type="submit" disabled={busy}>
          <Icon name="save" /> {busy ? 'Saving…' : 'Save'}
        </Button>
      </form>

      <Dialog
        open={pickerOpen}
        title="Copy a chapter from one of your books"
        onClose={() => setPickerOpen(false)}
        actions={(
          <>
            <Button variant="secondary" onClick={() => setPickerOpen(false)} disabled={pickerLoading}>
              Cancel
            </Button>
            <Button
              onClick={handleCopyChapter}
              disabled={!pickedBookId || !pickedChapterId || pickerLoading}
            >
              Copy chapter
            </Button>
          </>
        )}
      >
          {pickerLoading && <p role="status">Loading books…</p>}
          {!pickerLoading && sourceBooks && sourceBooks.length === 0 && (
            <Alert>
              You don't have any books with chapters yet.
            </Alert>
          )}
          {!pickerLoading && sourceBooks && sourceBooks.length > 0 && (() => {
            const pickedBook = sourceBooks.find((b) => b._id === pickedBookId);
            const versions = pickedBook ? pickedBook.versions || [] : [];
            const pickedVersion = versions.find((v) => v._id === pickedVersionId)
              || versions[0];
            const chapters = pickedVersion ? pickedVersion.chapters || [] : [];
            return (
              <div>
                <Field>
                  <label htmlFor="sample-source-book">Book</label>
                  <select
                    id="sample-source-book"
                    value={pickedBookId || ''}
                    onChange={(event) => {
                      const bookId = event.target.value || null;
                      const book = sourceBooks.find((b) => b._id === bookId);
                      setPickedBookId(bookId);
                      setPickedVersionId(book ? book.latestVersionId : null);
                      setPickedChapterId(null);
                    }}
                  >
                    <option value="">Pick a book</option>
                    {sourceBooks.map((book) => (
                      <option key={book._id} value={book._id}>
                        {book.title || '(Untitled book)'}
                      </option>
                    ))}
                  </select>
                </Field>
                {pickedBook && versions.length > 1 && (
                  <Field>
                    <label htmlFor="sample-source-version">Version</label>
                    <select
                      id="sample-source-version"
                      value={pickedVersion ? pickedVersion._id : ''}
                      onChange={(event) => {
                        setPickedVersionId(event.target.value || null);
                        setPickedChapterId(null);
                      }}
                    >
                      {versions.map((version, index) => {
                        const isLatest = version._id === pickedBook.latestVersionId;
                        const label = version.versionName ||
                          (version.versionNumber
                            ? `Version ${version.versionNumber}`
                            : `Version ${versions.length - index}`);
                        return (
                          <option key={version._id || `legacy-${index}`} value={version._id}>
                            {label}{isLatest ? ' (latest)' : ''}
                          </option>
                        );
                      })}
                    </select>
                  </Field>
                )}
                <Field>
                  <label htmlFor="sample-source-chapter">Chapter</label>
                  <select
                    id="sample-source-chapter"
                    value={pickedChapterId || ''}
                    disabled={!pickedBookId || chapters.length === 0}
                    onChange={(event) => setPickedChapterId(event.target.value || null)}
                  >
                    <option value="">Pick a chapter</option>
                    {chapters.map((chapter) => (
                      <option key={chapter._id} value={chapter._id}>
                        {chapter.title || '(Untitled chapter)'}
                        {chapter.wordCount ? ` — ${chapter.wordCount} words` : ''}
                      </option>
                    ))}
                  </select>
                </Field>
                <Alert>
                  The chapter's text will replace whatever you have in the
                  "Chapter text" field. The current title is kept unless empty.
                </Alert>
              </div>
            );
          })()}
      </Dialog>

      {!isNew && (!isNative || (sample && sample.audioUrl)) && (
        <Card>
          <h3>
            <Icon name="volume up" /> Audio (ElevenLabs TTS)
          </h3>
          {!voicesInfo.configured && (
            <Alert variant="warning">
              ElevenLabs is not configured on this server. Set BR_ELEVENLABS_API_KEY to enable audio generation.
            </Alert>
          )}
          {!isNative && <p>
            Generate a high-quality narration of this sample.{' '}
            {sample && sample.audioUrl && (
              <Badge>
                <Icon name="check" /> Audio ready
              </Badge>
            )}
            {sample && sample.ttsStatus === 'generating' && (
              <Badge>
                <Icon name="hourglass half" /> Generating…
              </Badge>
            )}
            {sample && sample.ttsStatus === 'failed' && (
              <Badge>
                <Icon name="warning" /> Failed: {sample.ttsError}
              </Badge>
            )}
          </p>}
          {!isNative && <Row>
            <select
              aria-label="Voice"
              value={voiceId || ''}
              onChange={(event) => setVoiceId(event.target.value || undefined)}
              disabled={!voicesInfo.configured || voiceOptions.length === 0}
            >
              <option value="">Pick a voice</option>
              {voiceOptions.map((voice) => (
                <option key={voice.voice_id} value={voice.voice_id}>{voice.name}</option>
              ))}
            </select>
            <Button
              onClick={handleGenerate}
              disabled={
                busy ||
                !voicesInfo.configured ||
                !sample ||
                sample.ttsStatus === 'generating'
              }
            >
              {sample && sample.audioUrl ? 'Regenerate audio' : 'Generate audio'}
            </Button>
          </Row>}
          {sample && sample.audioUrl && (
            <audio controls src={sample.audioUrl} style={{width: '100%'}} />
          )}
        </Card>
      )}

      {!isNew && stats && (
        <>
          <StatBox>
            <div>
              <strong>Ratings</strong>
              <div>{stats.sample.ratingsCount || 0}</div>
            </div>
            <div>
              <strong>Want to keep reading</strong>
              <div>
                {stats.sample.yesCount || 0} yes / {stats.sample.noCount || 0} no
              </div>
            </div>
            <div>
              <strong>Opted in for contact</strong>
              <div>{stats.sample.contactOptInCount || 0}</div>
            </div>
          </StatBox>

          {stats.siblings && stats.siblings.length > 0 && (
            <Card>
              <h3>A/B variants</h3>
              {stats.siblings.map((sib) => (
                <div key={sib._id}>
                  <Badge>Variant {sib.variantLabel}</Badge> {sib.title} —{' '}
                  {sib.yesCount}/{sib.ratingsCount} yes (
                  {sib.contactOptInCount} contact opt-ins){' '}
                  <Link to={`/sample-chapters/edit/${sib._id}`}>open</Link>
                </div>
              ))}
            </Card>
          )}

          <Card>
            <h3>
              <Icon name="mail" /> Readers who opted in for contact
            </h3>
            <p>
              These readers said yes to "would you keep reading?" and consented
              to be contacted when more material is ready. They can revoke at
              any time — once they do, they disappear from this list.
            </p>
            {contacts.length === 0 && <p>No opt-ins yet.</p>}
            {contacts.map((c) => (
              <ContactRow key={c.ratingId}>
                {c.user.photoUrl && (
                  <img src={c.user.photoUrl} alt="" width="32" height="32" />
                )}
                <ContactDetails>
                  <strong>{c.user.displayName}</strong>
                  <Muted>
                    <a href={`mailto:${c.user.email}`}>{c.user.email}</a>
                  </Muted>
                  {c.comment && <Quote>"{c.comment}"</Quote>}
                </ContactDetails>
                <Button
                  size="small"
                  onClick={() =>
                    setInvitee({
                      _id: c.user._id,
                      displayName: c.user.displayName,
                    })
                  }
                >
                  <Icon name="paper plane" /> Invite as beta reader
                </Button>
              </ContactRow>
            ))}
          </Card>

          {anonymousRatings.length > 0 && (
            <Card>
              <h3>
                <Icon name="comments" /> Other feedback
              </h3>
              <p>
                Answers from readers who did not opt in to be contacted. Their
                comments are yours to read; who wrote them is not shared.
              </p>
              <ul>
                {/* Keyed positionally: the API deliberately withholds each
                    row's id so a revoked reader cannot be re-linked to the
                    contact they used to appear as. */}
                {anonymousRatings.map((r, i) => (
                  <li key={i}>
                    <div>
                      <Badge>
                        {r.wantsToContinue ? 'Would keep reading' : 'Would not'}
                      </Badge>
                      {r.comment && <Quote>"{r.comment}"</Quote>}
                    </div>
                  </li>
                ))}
              </ul>
            </Card>
          )}
        </>
      )}

      {invitee && (
        <Dialog
          open
          title={`Invite ${invitee.displayName}`}
          onClose={() => setInvitee(null)}
          actions={<Button variant="secondary" onClick={() => setInvitee(null)}>Close</Button>}
        >
              {/* Same component the reader index uses, so book + version
                  selection, the reader cap and the custom message all behave
                  identically here. `source` tags the Reader so this funnel is
                  measurable. */}
              <InviteReader
                reader={invitee}
                source="sample_chapter_contact_opt_in"
                onClose={() => setInvitee(null)}
              />
        </Dialog>
      )}
    </Page>
  );
};

export default SampleChapterEditor;
